// Helper functions
import { LanguageCode } from '../types';

// Add a leading zero if the month or day only contain a single digit
export const padTo2Digits = (num: number): string => {
  return num.toString().padStart(2, '0');
};

// Format Date object to DD/MM/YY format
export const formatDate = (date: Date, isFullYear = false): string => {
  return [
    padTo2Digits(date.getDate()), // DD
    padTo2Digits(date.getMonth() + 1), // MM
    isFullYear ? String(date.getFullYear()) : String(date.getFullYear()).slice(2), // YY or YYYY
  ].join('/');
};

// Return a random string key.
export const getRandkey = (): string => {
  return Math.random().toString(36).substr(2, 5);
};

//Convert the lang code (fr/en) to a single letter format (F/E)
export const convertLangCodeToSingleLetter = (langCode: LanguageCode): 'F' | 'E' => {
  return langCode === 'fr' ? 'F' : 'E';
};

// Display elements outside of the react app.
export const displayOutsideComponents = (components = ''): void => {
  const compArr = components.split('|');

  if (!compArr) return;

  compArr.forEach(comp => {
    const compEl = document.getElementById(comp);
    if (compEl && compEl.classList.contains('visually-hidden')) {
      compEl.classList.remove('visually-hidden');
    }
  });
};

// Hide elements outside of the react app.
export const hideOutsideComponents = (components = ''): void => {
  const compArr = components.split('|');

  if (!compArr) return;

  compArr.forEach(comp => {
    const compEl = document.getElementById(comp);
    if (compEl && !compEl.classList.contains('visually-hidden')) {
      compEl.classList.add('visually-hidden');
    }
  });
};

// Display AR block of the target model outside of the react app.
export const displayOutsideARBlock = (modelName: string): void => {
  const targetARBlocks = document.querySelectorAll(`.mz-ar-block[data-model-name="${modelName}"]`);
  if (targetARBlocks.length > 0) {
    targetARBlocks.forEach(comp => {
      if (comp.classList.contains('visually-hidden')) {
        comp.classList.remove('visually-hidden');
      }
    });
  }
};

// Format phone number string (without country extension code) to XXX-XXX-XXXX format.
export const formatPhoneNumber = (phoneNumStr: string): string => {
  // Filter only numbers from the input
  const cleaned = ('' + phoneNumStr).replace(/\D/g, '');
  // Check if the input is of correct length
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    return match[1] + '-' + match[2] + '-' + match[3];
  }

  console.log('Error: phoneNumStr, ' + phoneNumStr, ', is not in the right format in formatPhoneNumber().');

  return phoneNumStr;
};

// Get date ordinal (st, nd, rd, th)
export const getOrdinalNum = (num: number): string => {
  if (num > 3 && num < 21) return 'th';
  switch (num % 10) {
    case 1: return "st";
    case 2: return "nd";
    case 3: return "rd";
    default: return "th";
  }
};

// Set item on localstorage.
export const setItemOnLocalStorage = <T>(key: string, item: T): void => {
  if (typeof (Storage) !== "undefined") {
    window.localStorage.setItem(key, JSON.stringify(item));
  } else {
    // No web storage support.
    console.log('Error: No web storage support.');
  }
};

// Get item from localstorage.
export const getItemOnLocalStorage = <T>(key: string): T | null => {
  if (typeof (Storage) !== "undefined") {
    const item = window.localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } else {
    // No web storage support.
    console.log('Error: No web storage support.');
    return null;
  }
};

// Auto-format phone number in input element.
export const autoFormatPhoneNumber = (value: string): string => {
  if (!value) return value;

  value = value.replace('+1', '');

  // clean the input for any non-digit values.
  const phoneNumber = value.replace(/[^\d]/g, '');

  const phoneNumberLength = phoneNumber.length;

  if (phoneNumberLength <= 3) return `${phoneNumber}`;

  if (phoneNumberLength <= 6) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }

  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)} ${phoneNumber.slice(6, 10)}`;
};

// Strip html element tags
export const stripHTML = (html: string): string => {
  if (!html) return '';
  return html.replace(/(<([^>]+)>)/gi, "").replace('&nbsp;', ' ');
};

// Open a modal.
export const openModal = (targetId: string): void => {
  const modal = document.getElementById(targetId);
  if (modal) {
    modal.setAttribute('aria-hidden', 'false');
    // Focus the first element within the modal.
    const focusable_selectors = 'a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), *[tabindex]';
    const focusableElement = modal.querySelector(focusable_selectors);
    if (focusableElement instanceof HTMLElement) {
      focusableElement.focus();
    }
    // Make the body unscrollable.
    handleBodyScrollHidden(true);
  }
};

// Close a modal.
export const closeModal = (modal: HTMLElement | null): void => {
  if (modal) {
    modal.setAttribute('aria-hidden', 'true');
    handleBodyScrollHidden(false);
    
    setTimeout(() => {
      const submitButton = modal.querySelector('button[type="submit"]');
      if (submitButton instanceof HTMLButtonElement) {
        submitButton.disabled = false;
      }
    }, 1200);
  }
};

// Handle body "unscrollable" class.
export const handleBodyScrollHidden = (isHidden: boolean): void => {
  if (isHidden) {
    document.body.classList.add('disabled');
  } else {
    document.body.classList.remove('disabled');
  }
};

// Convert minutes to hours and minutes.
export const convertMinutesToHours = (totalMinutes: number): { hours: number; minutes: number } | undefined => {
  if (!totalMinutes) return;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return {
    hours,
    minutes
  };
};

// Scroll to the top of the page
export const scrollToTop = (): void => {
  window.scrollTo(0, 0);
};

// Encrypts a string with SHA-256 one-way encryption.
export const sha256 = async (message: string): Promise<string> => {
  // encode as (utf-8) Uint8Array
  const msgUint8 = new TextEncoder().encode(message);
  // hash the message
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
  // convert buffer to byte array
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  // convert bytes to hex string
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
};

// Extend Window interface for dataLayer
declare global {
  interface Window {
    dataLayer: any[];
    gsap: any;
  }
}

// Push data to GTM dataLayer.
export const pushToGTMDataLayer = (data: Record<string, any>): void => {
  try {
    window.dataLayer.push(data);
  } catch (err) {
    console.log('Error: dataLayer is not found in pushToGTMDataLayer().');
  }
};

// Analytics data interface
interface AnalyticsData {
  email?: string;
  firstname?: string;
  lastname?: string;
  phone?: string;
  postalcode?: string;
  modelCarlineName?: string;
  modelYear?: number;
  vin?: string;
  kilometers?: number;
  msrp?: number;
  dealerName?: string;
  optin?: string;
}

// Handle adding analytics tracking attributes.
export const handleAnalyticsTrackingCodes = async (
  data: AnalyticsData,
  dataEvent?: string,
  dataForm?: string,
  dataFormId?: string
): Promise<void> => {
  try {
    const dataContent: Record<string, any> = {};
    if (dataEvent) dataContent.event = dataEvent;
    if (dataForm) dataContent.form = dataForm;
    if (dataFormId) dataContent.formid = dataFormId;
    
    if (data.email) dataContent.userid = await sha256(data.email.toLowerCase().trim());
    if (data.firstname) dataContent.firstname = await sha256(data.firstname.toLowerCase().trim());
    if (data.lastname) dataContent.lastname = await sha256(data.lastname.toLowerCase().trim());
    if (data.phone) dataContent.phone = await sha256(data.phone);
    if (data.postalcode) dataContent.postalcode = data.postalcode;

    if (data.modelCarlineName) dataContent.model = data.modelCarlineName;
    if (data.modelYear) dataContent.year = data.modelYear;
    if (data.vin) dataContent.vin = data.vin;
    if (data.kilometers) dataContent.odometer = data.kilometers;
    if (data.msrp) dataContent.price = data.msrp;

    if (data.dealerName) dataContent.dealer = data.dealerName;

    if (data.optin) dataContent.formtype = data.optin;
    
    console.log('handleAnalyticsTrackingCodes', dataContent);
    pushToGTMDataLayer(dataContent);

  } catch (err) {
    console.log(err);
  }
};

// GSAP ScrollTo options interface
interface ScrollToOptions {
  y: HTMLElement | number;
  offsetY?: number;
}

// Scroll to element using gsap (loaded in Core)
// usage: scrollTo({ y: ref.current, offsetY: 100 })
export const scrollTo = (options: ScrollToOptions): void => {
  if (window.gsap) {
    window.gsap.to(window, {
      duration: .5,
      scrollTo: options
    });
  } else {
    console.error('gsap is not found in scrollTo()');
  }
};

// Block +,- and e in Number Input
export const blockInvalidCharInNumberField = (e: React.KeyboardEvent): void => {
  if (['e', 'E', '+', '-'].includes(e.key)) {
    e.preventDefault();
  }
};

// Convert text to title case.
export const titleCase = (str: string): string | undefined => {
  if (!str) return;
  return str.toLowerCase().split(' ').map(function(word) {
      return (word.charAt(0).toUpperCase() + word.slice(1));
  }).join(' ');
};

// Convert text to sentence case.
export const sentenceCase = (str: string): string | undefined => {
  if (!str) return;
  console.log(str, str.substring(0, 1).toUpperCase(), str.toLowerCase().substring(1));
  return str.substring(0, 1).toUpperCase() + str.toLowerCase().substring(1);
};

// Get base url.
export const getBaseUrl = (): string => {
  if (window.location.host.indexOf("localhost") !== -1) {
    return "https://preprod.mazda.ca";
  } else if (window.location.host.indexOf("mzca03mstre4u53inte.dxcloud.episerver.net") !== -1) {
    return "https://preprod.mazda.ca";
  }
  return window.location.protocol + '//' + window.location.host;
};

// Convert currency to localized format
export const convertCurrency = (money: number, lang: LanguageCode): string => {
	const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';
	return Number(money).toLocaleString(locale, { 
    style: 'currency', 
    currency: 'CAD', 
    minimumFractionDigits: 0, 
    maximumFractionDigits: 0
  });
};

// Convert kilometers to localized format
export const convertKM = (distance: number, lang: LanguageCode): string => {
	const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';
	const kmFormat = lang === 'fr' ? ' km' : 'KM';
	return Number(distance).toLocaleString(locale, { 
    minimumFractionDigits: 0, 
    maximumFractionDigits: 0
  }) + kmFormat;
};

// Capitalize all words in a string
export const capitalizeWords = (str: string): string => {
  // Convert the string to lowercase
  str = str.toLowerCase();

  // Split the string into an array of words
  let words = str.split(" ");
  
  // Capitalize the first letter of each word
  for (let i = 0; i < words.length; i++) {
    words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1);
  }
  
  // Join the words back into a single string
  return words.join(" ");
};
